import { getTestBySlug, type GuestTestResult } from '@/data/guestTests';
import { getStoredGuestResults } from '@/utils/guestQuizMapping';
import { logger } from '@/utils/structuredLogging';

const GUEST_RESULTS_KEY = 'psyforge:anon:guest_quiz_results';
const MIGRATION_KEY = 'psyforge:migrations:cf-stack-v1';
const SLUG = 'cognitive-functions';

// Detect broken cached results: XXXX, or dom/aux on the same axis.
const SAME_AXIS_PAIRS = /^(Te-Fe|Fe-Te|Ti-Fi|Fi-Ti|Ne-Se|Se-Ne|Ni-Si|Si-Ni|Te-Ti|Ti-Te|Fe-Fi|Fi-Fe|Ne-Ni|Ni-Ne|Se-Si|Si-Se)\b/;

function isBroken(result: GuestTestResult): boolean {
  const name = `${result.primaryLabel ?? ''} ${(result as unknown as { matchName?: string }).matchName ?? ''}`;
  if (/XXXX/i.test(name)) return true;
  if (SAME_AXIS_PAIRS.test(result.primaryLabel ?? '')) return true;
  return false;
}

/**
 * One-time client migration: rewrites cached Cognitive Functions guest
 * results using the canonical Jungian derivation. Users who took the quiz
 * before the fix would otherwise still see "Te-Fe Stack (XXXX)".
 */
export function fixCognitiveFunctionsStack(): void {
  try {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(MIGRATION_KEY)) return;

    const stored = getStoredGuestResults();
    if (!stored.length) {
      localStorage.setItem(MIGRATION_KEY, new Date().toISOString());
      return;
    }

    const test = getTestBySlug(SLUG);
    if (!test) {
      localStorage.setItem(MIGRATION_KEY, new Date().toISOString());
      return;
    }

    let changed = 0;
    const rewritten = stored.map((r) => {
      if (r.testSlug !== SLUG) return r;
      if (!isBroken(r)) return r;
      if (!Array.isArray(r.scales) || r.scales.length === 0) return r;

      const scoreMap: Record<string, number> = {};
      for (const s of r.scales) scoreMap[s.key] = s.score ?? 0;

      const fixed = test.getResult(scoreMap);
      changed++;
      return {
        ...r,
        primaryLabel: fixed.label,
        primaryDescription: fixed.description,
        emoji: fixed.emoji ?? r.emoji,
        strengths: fixed.strengths ?? r.strengths,
        growthAreas: fixed.growthAreas ?? r.growthAreas,
        insight: fixed.insight ?? r.insight,
        // matchName is used by some downstream renderers
        ...(('matchName' in r) ? { matchName: fixed.label } : {}),
        _migrated: 'cf-stack-v1',
      } as GuestTestResult;
    });

    if (changed > 0) {
      localStorage.setItem(GUEST_RESULTS_KEY, JSON.stringify(rewritten));
      logger.info('Rewrote cognitive-functions guest results', {
        component: 'fixCognitiveFunctionsStack',
        metadata: { rewritten: changed },
      });
    }

    localStorage.setItem(MIGRATION_KEY, new Date().toISOString());
  } catch (err) {
    // Never let a migration break app boot.
    try {
      logger.warn('cf-stack migration failed', {
        component: 'fixCognitiveFunctionsStack',
        metadata: { error: String(err) },
      });
    } catch { /* silent */ }
  }
}
